<template>
  <!--
    Note: the section card used to expose a "Manage in Alerts" header link
    pointing to a standalone `/alerts` page. That page no longer exists
    (its URL now redirects to `/my-environment/alerts` — i.e. this very
    component when not in snapshot mode), so the header link would either
    be self-referential or be redundant with the Alerts entry already in
    the Mijn-omgeving sidebar. Removed entirely.
  -->
  <section-card
    :title="embedded ? t('mijn_omgeving.alerts.title') : null"
    :subtitle="embedded ? t('mijn_omgeving.alerts.subtitle') : null"
    :is-empty="!isLocked && !isResolving && !hasItems && !isLoading"
    :is-loading="isResolving || (isLoading && !hasItems)"
    :error="isLocked ? null : error || null"
    :empty-title="t('mijn_omgeving.alerts.empty_title')"
    :empty-message="emptyStateMessage"
  >
    <!-- Skeleton: top-N ghost rows matching the snapshot list — status pill +
         name fill + relative-time. Same shape as the real rows so the
         column height holds steady while alerts load. -->
    <template #loadingSkeleton>
      <ul class="divide-y divide-theme-border dark:divide-theme-border -my-1" aria-hidden="true">
        <li v-for="i in 3" :key="`alert-skel-${i}`" class="flex items-center gap-2 py-2 min-w-0">
          <skeleton-text width="44px" height="14px" />
          <div class="flex-1 min-w-0">
            <skeleton-text :width="i === 1 ? '65%' : i === 2 ? '80%' : '50%'" height="13px" />
          </div>
          <skeleton-text width="28px" height="10px" />
        </li>
      </ul>
    </template>

    <!-- No `tool.alerts.use`: a locked teaser instead of any API call. The
         passive list used to 403 into the global upgrade modal on Home. -->
    <alerts-locked-teaser
      v-if="isLocked"
      compact
      :source="snapshot ? 'home_card' : 'my_environment_card'"
    />

    <!-- Snapshot mode (Home Layer 3): compact clickable list of top-N alerts,
         most recently triggered first then most recently created. Each row
         summarises name + status + relative time and links to the alert in the
         alerts list (?alert=<id>: scrolled to, highlighted and focused), the
         same target as the alert mails and notifications. -->
    <template v-else-if="snapshot">
      <ul v-if="snapshotItems.length" class="divide-y divide-theme-border dark:divide-theme-border -my-1">
        <li v-for="alert in snapshotItems" :key="alert.id" class="min-w-0">
          <router-link
            :to="{ path: '/my-environment/alerts', query: { alert: alert.id } }"
            class="flex items-center gap-2 min-w-0 py-2 -mx-2 px-2 rounded-md transition-colors hover:bg-theme-palette-emerald/40 dark:hover:bg-theme-palette-emerald/15 group"
          >
            <span
              :class="[
                'shrink-0 inline-flex items-center text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded-full',
                snapshotStatusClass(alert),
              ]"
            >
              {{ snapshotStatusLabel(alert) }}
            </span>
            <span
              class="flex-1 min-w-0 text-sm text-theme-text truncate group-hover:text-theme-palette-emerald dark:group-hover:text-theme-palette-emerald"
            >
              {{ alert.name || t('mijn_omgeving.alerts.untitled') }}
            </span>
            <span class="shrink-0 text-[11px] text-theme-text-muted tabular-nums">
              {{ snapshotTimeLabel(alert) }}
            </span>
          </router-link>
        </li>
      </ul>
      <!-- Falls back to the section-card empty state when items is empty —
           handled by `is-empty` on the parent. -->
    </template>

    <!-- Full table mode (Mijn omgeving → alerts). -->
    <div v-else class="space-y-1">
      <alert-row
        v-for="alert in displayed"
        :key="alert.id"
        :title="alert.name || t('mijn_omgeving.alerts.untitled')"
        :dataset="datasetLabel(alert)"
        :threshold="thresholdLabel(alert)"
        :delivery="deliveryLabel(alert)"
        :active="_isActive(alert)"
        :last-triggered="_lastTriggeredAt(alert)"
        @toggle="(v) => onToggle(alert, v)"
        @edit="onEdit(alert)"
        @delete="onDelete(alert)"
      />
    </div>

    <template v-if="!snapshot && hasItems" #footer>
      <p class="text-xs text-theme-text-secondary">
        {{ t('mijn_omgeving.alerts.home_hint') }}
      </p>
    </template>

    <template v-if="embedded" #emptyAction>
      <div class="flex max-w-full flex-wrap items-center justify-center gap-2">
        <AppButton
          v-for="suggestion in visibleSuggestedAlerts"
          :key="suggestion.id"
          data-test="alert-suggestion"
          variant="outline"
          tone="primary"
          size="sm"
          :icon="mdiPlus"
          :label="suggestion.label"
          @click="createFromTemplate(suggestion)"
        />
        <AppButton
          :to="{ name: 'alerts.create', query: { entry: 'my_environment_card' } }"
          :variant="visibleSuggestedAlerts.length ? 'soft' : undefined"
          :tone="visibleSuggestedAlerts.length ? 'neutral' : 'primary'"
          size="sm"
          :icon="mdiPlus"
          :label="
            visibleSuggestedAlerts.length
              ? t('mijn_omgeving.alerts.create_custom')
              : t('mijn_omgeving.alerts.create')
          "
        />
      </div>
    </template>
  </section-card>

  <confirm-modal
    :visible="!!deleteTarget"
    :title="t('alerts.confirm_delete_title')"
    :message="t('mijn_omgeving.alerts.confirm_delete', { name: deleteTarget?.name || '' })"
    :confirm-label="t('alerts.confirm_delete_button')"
    :cancel-label="t('alerts.confirm_cancel_button')"
    confirm-color="red"
    @confirm="confirmDelete"
    @cancel="deleteTarget = null"
  />
</template>

<script setup>
  /* eslint-disable no-undef */
  import ConfirmModal from '@/components/ConfirmModal.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import AlertRow from '@/components/ui/data-display/AlertRow.vue';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import { useAlertConditionMeta } from '@/composables/useAlertConditionMeta';
  import { useAlertsAccess } from '@/composables/useAlertsAccess';
  import { useNotification } from '@/composables/useNotification';
  import { useProfile } from '@/composables/profile';
  import { alertConditionText } from '@/pages/alerts/alertFormat';
  import AlertsLockedTeaser from '@/pages/alerts/components/AlertsLockedTeaser.vue';
  import AlertRepository from '@/repositories/AlertRepository';
  import { mdiPlus } from '@mdi/js';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const props = defineProps({
    limit: { type: Number, default: null },
    /** When true, render the compact count-line snapshot for the Home Layer 3. */
    snapshot: { type: Boolean, default: false },
    /** When `true` (default), the section-card shows its own title/subtitle.
     *  When `false`, the title is hidden — used when the section is the
     *  full-page content of `/my-environment/alerts`, where the page-level
     *  `StandardToolPage` header already identifies the section. */
    embedded: { type: Boolean, default: true },
  });

  const router = useRouter();
  const { t, te } = useI18n();
  const { notifySuccess, notifyError } = useNotification();
  const { profile } = useProfile();
  const { canAttemptLoad, canUseAlerts, isLocked, isResolving } = useAlertsAccess();
  const conditionMeta = useAlertConditionMeta();

  // Role-preset alert suggestions — surfaced in the empty state as a nudge to
  // set up the alerts that matter for the user's market role. Each links into
  // the alert wizard prefilled with the matching template (the id mirrors a
  // real system template in datasets/alert_templates.yml).
  const suggestedAlerts = computed(() => profile.value?.suggested_alert_templates || []);
  const visibleSuggestedAlerts = computed(() => (props.snapshot ? [] : suggestedAlerts.value));
  const emptyStateMessage = computed(() =>
    visibleSuggestedAlerts.value.length ? '' : t('mijn_omgeving.alerts.empty_message')
  );

  function createFromTemplate(suggestion) {
    if (!suggestion?.id) return;
    router.push({
      name: 'alerts.create',
      query: { template: suggestion.id, entry: 'suggested_template' },
    });
  }

  const items = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  async function load() {
    isLoading.value = true;
    error.value = null;
    try {
      // Alerts-ship (@stoep) backend client: `list` returns
      // `{ data: { alerts: [...], total, active_count, ... } }`. This snapshot
      // widget just needs the alert array to power the top-N list on Home /
      // Mijn-omgeving overview; the full management UI lives in AlertIndex.
      // Passive call: a 403 here (stale entitlement) must not open the global
      // upgrade modal — the card just shows its own error line.
      const response = await AlertRepository.list(undefined, { suppressEntitlementModal: true });
      const rows = response?.data?.alerts;
      items.value = Array.isArray(rows) ? rows : [];
    } catch (e) {
      error.value = t('mijn_omgeving.alerts.load_error');
      items.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  // Only talk to /v1/alerts once the entitlement is known to be granted (or
  // could not be determined — then the card's own error line is the fallback);
  // the watcher also covers the access set resolving after mount.
  watch(
    canAttemptLoad,
    (allowed) => {
      if (allowed) load();
    },
    { immediate: true }
  );
  // Access outage recovery: the load attempted while access was unavailable
  // failed, and the entitlement has now resolved to granted — try again. (When
  // it resolves to locked, the teaser takes over and the stale error is hidden.)
  watch(canUseAlerts, (granted, was) => {
    if (granted && !was && error.value) load();
  });

  const displayed = computed(() => {
    if (props.limit) return items.value.slice(0, props.limit);
    return items.value;
  });

  // Snapshot list (Home Layer 3): top-N alerts sorted by most recent activity.
  // Triggered alerts surface first, then the most recently created. Cap at
  // `props.limit ?? 3` so the column doesn't blow up vertically.
  const snapshotItems = computed(() => {
    const max = props.limit ?? 3;
    const arr = [...(items.value || [])];
    arr.sort((a, b) => _activityTs(b) - _activityTs(a));
    return arr.slice(0, max);
  });

  // Runtime state is nested: the API returns `alert.state.last_triggered_at`,
  // there is no top-level `last_triggered_at`.
  function _lastTriggeredAt(alert) {
    return alert?.state?.last_triggered_at || null;
  }

  function _activityTs(alert) {
    const ts = _lastTriggeredAt(alert) || alert?.created_at || alert?.updated_at;
    if (!ts) return 0;
    const ms = new Date(ts).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  const hasItems = computed(() => {
    if (props.snapshot) return snapshotItems.value.length > 0;
    return displayed.value.length > 0;
  });

  // Status pill helpers for the snapshot rows — keep the visual language
  // consistent with ToolRunRow (sky=running, rose=failed, emerald=ready).
  function _isActive(alert) {
    // The alert resource (/v1/alerts) carries `is_active`; older snapshot
    // shapes used `!paused`. There is no `enabled` field on the API.
    if (typeof alert?.is_active === 'boolean') return alert.is_active;
    if (typeof alert?.paused === 'boolean') return !alert.paused;
    return true;
  }
  function _triggeredToday(alert) {
    const at = _lastTriggeredAt(alert);
    if (!at) return false;
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d >= today;
  }

  function snapshotStatusLabel(alert) {
    if (_triggeredToday(alert)) return t('mijn_omgeving.alerts.status_triggered');
    if (!_isActive(alert)) return t('mijn_omgeving.alerts.status_paused');
    return t('mijn_omgeving.alerts.status_active');
  }
  function snapshotStatusClass(alert) {
    if (_triggeredToday(alert))
      return 'bg-theme-palette-rose/10 text-theme-palette-rose dark:bg-theme-palette-rose/30 dark:text-theme-palette-rose';
    if (!_isActive(alert))
      return 'bg-theme-bg-subtle text-theme-text-secondary dark:bg-theme-surface-raised';
    return 'bg-theme-palette-emerald/10 text-theme-palette-emerald dark:bg-theme-palette-emerald/30 dark:text-theme-palette-emerald';
  }

  // Compact relative timestamp ("2u", "3d") so the row stays single-line in
  // the narrow snapshot column. Falls back to a localized date for anything
  // older than 7 days.
  function snapshotTimeLabel(alert) {
    const ts = _activityTs(alert);
    if (!ts) return '';
    const diffMs = Date.now() - ts;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return t('mijn_omgeving.alerts.time_now');
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}u`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(ts).toLocaleDateString();
  }

  // Row labels come from the alert document itself: titles the backend stamps
  // on `target`, the backend's condition sentence (shared formatter as the
  // fallback), and `notification.channels`.
  function datasetLabel(alert) {
    const target = alert?.target || {};
    const dataset = target.dataset_title || target.dataset_id || '';
    const series = target.series_title || target.series_id || '';
    return [dataset, series !== dataset ? series : ''].filter(Boolean).join(' · ');
  }
  function thresholdLabel(alert) {
    return alertConditionText(alert, t, conditionMeta.label);
  }
  function deliveryLabel(alert) {
    const channels = alert?.notification?.channels;
    if (!Array.isArray(channels)) return '';
    // Legacy `sms` was folded into email (as in AlertReview / AlertNotification);
    // a channel without a translation is skipped, never shown as a raw key.
    const labels = [];
    for (const raw of channels) {
      const key = `notification_preferences.channels.${raw === 'sms' ? 'email' : raw}`;
      const label = te(key) ? t(key) : '';
      if (label && !labels.includes(label)) labels.push(label);
    }
    return labels.join(', ');
  }

  async function onToggle(alert, nextActive) {
    // Optimistic toggle on the API's `is_active` flag (same field AlertIndex
    // uses); if the request fails, revert and let the next load() reconcile.
    const prev = alert.is_active;
    alert.is_active = nextActive;
    try {
      await AlertRepository.update(alert.id, { is_active: nextActive });
      notifySuccess(t(nextActive ? 'messages.alert_resumed' : 'messages.alert_paused'));
    } catch (_) {
      alert.is_active = prev; // revert
      notifyError(t('messages.alert_save_failed'));
    }
  }

  function onEdit(alert) {
    router.push(`/alerts/${alert.id}/edit`);
  }

  // Delete goes through the shared ConfirmModal (as on the alerts page).
  const deleteTarget = ref(null);

  function onDelete(alert) {
    if (!alert?.id) return;
    deleteTarget.value = alert;
  }

  async function confirmDelete() {
    const alert = deleteTarget.value;
    deleteTarget.value = null;
    if (!alert?.id) return;
    try {
      await AlertRepository.destroy(alert.id);
      items.value = items.value.filter((a) => a.id !== alert.id);
      notifySuccess(t('messages.alert_deleted'));
    } catch (_) {
      // Reload to reconcile any partial failure.
      await load();
      notifyError(t('messages.alert_delete_failed'));
    }
  }
</script>
